// Retorna uma função de handleChange utilizando um setter e um parser para converter o valor do input.
export const createHandleChange = <T>(
	setState: (value: T) => void,
	parser: (value: string) => T = (v) => v as unknown as T // parser default: identidade
) => {
	return (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setState(parser(value));
	};
};

// Versão específica para onBlur (FocusEvent)
export const createHandleBlur = <T>(
	setState: (value: T) => void,
	parser: (value: string) => T = (v) => v as unknown as T // parser default: identidade
) => {
	return (e: React.FocusEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setState(parser(value));
	};
};

// parser para valores monetários (câmbio de centavos para reais)
export const currencyParser = (value: string): number => {
	const numeric = value.replace(/\D/g, '');
	return Number(numeric) / 100;
};

// Parser para datas, convertendo string para uma data ISO
export const dateParser = (value: string): string => {
	if (!value) return '';
	const date = new Date(value);
	return date.toISOString();
};

// Parser específico para campos de data em formulários (retorna no formato YYYY-MM-DD)
export const dateInputParser = (value: string): string => {
	if (!value) return '';
	return value; // O input de data já retorna no formato correto
};

// Parser para texto com trim automático
export const trimTextParser = (value: string): string => {
	return value.trim();
};

// Parser para texto em maiúsculo
export const upperCaseParser = (value: string): string => {
	return value.toUpperCase();
};

// Parser para números inteiros
export const integerParser = (value: string): number => {
     return parseInt(value, 10) || 0;
};

// Parser para números inteiros com validação de range
export const integerRangeParser = (min: number, max: number, defaultValue: number) => (value: string): number => {
     const num = parseInt(value, 10);
     if (isNaN(num) || value === '') return defaultValue;
     return Math.min(Math.max(num, min), max);
};

// Parser para telefone que aplica formatação automaticamente
export const phoneParser = (value: string): string => {
     const cleanPhone = value.replace(/\D/g, '');
     
     if (cleanPhone.length <= 2) {
          return cleanPhone;
     } else if (cleanPhone.length <= 3) {
          return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(2)}`;
     } else if (cleanPhone.length <= 7) {
          return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(2, 3)} ${cleanPhone.slice(3)}`;
     } else {
          return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(2, 3)} ${cleanPhone.slice(3, 7)}-${cleanPhone.slice(7, 11)}`;
     }
};

// Parser para CEP que aplica formatação automaticamente
export const cepParser = (value: string): string => {
     const cleanCep = value.replace(/\D/g, '');
     
     if (cleanCep.length <= 5) {
          return cleanCep;
     } else {
          return `${cleanCep.slice(0, 5)}-${cleanCep.slice(5, 8)}`;
     }
};

// Parser para moeda formatada em tempo real (formato brasileiro)
export const currencyFormatterParser = (value: string): { formatted: string; numeric: number } => {
	// Remove tudo exceto números
	const numeric = value.replace(/\D/g, '');
	
	if (!numeric || numeric === '0') {
		return { formatted: '', numeric: 0 };
	}
	
	// Converte para número (em centavos para reais)
	const numericValue = Number(numeric) / 100;
	
	// Formata no padrão brasileiro
	const formatted = new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
	}).format(numericValue);
	
	return { formatted, numeric: numericValue };
};

// Parser para CPF que aplica formatação automaticamente
export const cpfParser = (value: string): string => {
     const cleanCpf = value.replace(/\D/g, '');
     
     if (cleanCpf.length <= 3) {
          return cleanCpf;
     } else if (cleanCpf.length <= 6) {
          return `${cleanCpf.slice(0, 3)}.${cleanCpf.slice(3)}`;
     } else if (cleanCpf.length <= 9) {
          return `${cleanCpf.slice(0, 3)}.${cleanCpf.slice(3, 6)}.${cleanCpf.slice(6)}`;
     } else {
          return `${cleanCpf.slice(0, 3)}.${cleanCpf.slice(3, 6)}.${cleanCpf.slice(6, 9)}-${cleanCpf.slice(9, 11)}`;
     }
};

// Parser para CNPJ que aplica formatação automaticamente
export const cnpjParser = (value: string): string => {
     const cleanCnpj = value.replace(/\D/g, '');
     
     if (cleanCnpj.length <= 2) {
          return cleanCnpj;
     } else if (cleanCnpj.length <= 5) {
          return `${cleanCnpj.slice(0, 2)}.${cleanCnpj.slice(2)}`;
     } else if (cleanCnpj.length <= 8) {
          return `${cleanCnpj.slice(0, 2)}.${cleanCnpj.slice(2, 5)}.${cleanCnpj.slice(5)}`;
     } else if (cleanCnpj.length <= 12) {
          return `${cleanCnpj.slice(0, 2)}.${cleanCnpj.slice(2, 5)}.${cleanCnpj.slice(5, 8)}/${cleanCnpj.slice(8)}`;
     } else {
          return `${cleanCnpj.slice(0, 2)}.${cleanCnpj.slice(2, 5)}.${cleanCnpj.slice(5, 8)}/${cleanCnpj.slice(8, 12)}-${cleanCnpj.slice(12, 14)}`;
     }
};

// Parser dinâmico para CPF/CNPJ baseado no tipo
export const documentParser = (tipo: 'CPF' | 'CNPJ') => (value: string): string => {
     return tipo === 'CPF' ? cpfParser(value) : cnpjParser(value);
};